export const AUTH_ROLES = ["FOUNDER" , "INVESTOR"] as const;

export type AuthRole = (typeof AUTH_ROLES)[number];

export const JOURNEY_STEP_KEYS = {
  personalInfo: "personal-info",
  aboutYou: "about-you",
  welcome: "welcome",
  questions: "questions",
  introduction: "introduction",
  planSelection: "plan-selection",
  payment: "payment",
} as const;

export type JourneyStepKey =
  (typeof JOURNEY_STEP_KEYS)[keyof typeof JOURNEY_STEP_KEYS];

export interface JourneyStep {
  key: JourneyStepKey;
  label: string;
}

export const DEFAULT_AUTH_ROLE: AuthRole = "FOUNDER";

export const AUTH_ROUTE_PATHS = {
  getStarted: "/get-started",
  roleSelection: "/role-selection",
  signUp: "/signup/:role",
  signIn: "/sign-in",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
  legacyLogin: "/login",
  welcome: "/welcome/:role",
  questions: "/questions/:role",
  introduction: "/introduction/:role",
  membership: "/membership/:role",
  paymentSuccessful: "/payment-successful/:role",
  waitlist: "/waitlist",
  rejected: "/rejected",
} as const;

export const isAuthRole = (value?: string | null): value is AuthRole =>
  AUTH_ROLES.includes(value as AuthRole);

export const resolveAuthRole = (value?: string | null): AuthRole | null =>
  isAuthRole(value) ? value : null;

export const getSignUpPath = (role: AuthRole) => `/signup/${role}`;

export const getSignInPath = () => AUTH_ROUTE_PATHS.signIn;

export const SIGN_IN_COPY = {
  headingStart: "Welcome back to",
  headingMiddle: "your",
  headingAccentFounder: "founder &",
  headingAccentInvestor: "Investor",
  headingEnd: "network",
  subtitle: "Log in continue discovering events curated for your interests and location. Your personalized founder experience awaits.",
  emailPlaceholder: "Enter your email",
};

export const getJourneyPath = (stepKey: JourneyStepKey, role: AuthRole) => {
  switch (stepKey) {
    case JOURNEY_STEP_KEYS.welcome:
      return `/welcome/${role}`;
    case JOURNEY_STEP_KEYS.questions:
      return `/questions/${role}`;
    case JOURNEY_STEP_KEYS.introduction:
      return `/introduction/${role}`;
    // case JOURNEY_STEP_KEYS.planSelection:
    //   return `/membership/${role}`;
    // case JOURNEY_STEP_KEYS.payment:
    //   return `/payment-successful/${role}`;
    default:
      return getSignUpPath(role);
  }
};

export const getJourneyStepIndex = (steps: JourneyStep[], stepKey: JourneyStepKey) =>
  Math.max(
    steps.findIndex((step) => step.key === stepKey),
    0,
  );

export interface RoleContent {
  id: AuthRole;
  label: string;
  selectionDescription: string;
  signInHeading: string;
  signInSubheading: string;
  signUpSubheading?: string;
  signInBackground: string;
  signInStory: string[];
  signInEmailPlaceholder: string;
  signUpHeadingStart: string;
  signUpHeadingAccent: string;
  signUpHeadingEnd: string;
  signUpSteps: JourneyStep[];
  sampleData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    location?: string;
    addressType?: string;
    companyName?: string;
    linkedin?: string;
  };
}

export const ROLE_CONTENT: Record<AuthRole, RoleContent> = {
  FOUNDER: {
    id: "FOUNDER",
    label: "Founder",
    selectionDescription:
      "Choose this if you're building a startup or leading a business.",
    signInHeading: "Founder",
    signInSubheading: "Sign in to continue to BFC.",
    signInBackground: "/Foundersbg.png",
    signInStory: [
      "Great founders.",
      "Meaningful dinners.",
      "Stronger future.",
    ],
    signInEmailPlaceholder: "Enter your email",
    signUpHeadingStart: "Tell us a bit ",
    signUpHeadingAccent: "About",
    signUpHeadingEnd: " yourself",
    signUpSteps: [
      { key: JOURNEY_STEP_KEYS.personalInfo, label: "Personal Info" },
      { key: JOURNEY_STEP_KEYS.aboutYou, label: "About You" },
      { key: JOURNEY_STEP_KEYS.welcome, label: "Welcome" },
      { key: JOURNEY_STEP_KEYS.questions, label: "Questions" },
      { key: JOURNEY_STEP_KEYS.introduction, label: "Introduction" },
      // { key: JOURNEY_STEP_KEYS.planSelection, label: "Subscription Plan" },
      // { key: JOURNEY_STEP_KEYS.payment, label: "Payment" },
    ],
    sampleData: {
      firstName: "Enter your first name",
      lastName: "Enter your last name",
      email: "Enter your email",
      phone: "Enter your mobile number",
      location: "City, Country",
      addressType: "Home",
      companyName: "Enter your company name",
      linkedin: "https://www.linkedin.com/in/john-smith/",
    },
  },
  INVESTOR: {
    id: "INVESTOR",
    label: "Investor",
    selectionDescription:
      "Choose this if you invest in startups, businesses, or growth opportunities.",
    signInHeading: "Investor",
    signInSubheading: "Sign in to continue to BFC.",
    signInBackground: "/Investorsbg.png",
    signInStory: [
      "Curated dinners.",
      "Relevant connections.",
      "Smarter investing.",
    ],
    signInEmailPlaceholder: "Enter your email",
    signUpHeadingStart: "Tell us a bit ",
    signUpHeadingAccent: "About",
    signUpHeadingEnd: " yourself",
    signUpSteps: [
      { key: JOURNEY_STEP_KEYS.personalInfo, label: "Personal Info" },
      { key: JOURNEY_STEP_KEYS.aboutYou, label: "About You" },
      { key: JOURNEY_STEP_KEYS.welcome, label: "Welcome" },
      { key: JOURNEY_STEP_KEYS.questions, label: "Questions" },
      { key: JOURNEY_STEP_KEYS.introduction, label: "Introduction" },
      // { key: JOURNEY_STEP_KEYS.planSelection, label: "Subscription Plan" },
      // { key: JOURNEY_STEP_KEYS.payment, label: "Payment" },
    ],
    sampleData: {
      firstName: "Enter your first name",
      lastName: "Enter your last name",
      email: "Enter your email",
      phone: "Enter your mobile number",
      // location: "Mumbai, India",
      // addressType: "Office",
      // companyName: "Venture Capital Partners",
      linkedin: "Enter your LinkedIn profile URL",
    },
  },
};

export const GET_STARTED_COPY = {
  heading: "Where Founders & Investors Grow Together",
  ctaLabel: "Get Started",
  signInLabel: "I already have an account",
  legalPrefix: "By signing up you agree to our",
  termsLabel: "terms of service",
  privacyLabel: "privacy policy",
};

export const ROLE_SELECTION_COPY = {
  eyebrow: "WELCOME TO BFC",
  headingStart: "India's largest",
  headingAccent: "Founder",
  headingMiddle: "network",
  headingEnd: "starts here",
  body: "Register to apply to the BFC community \n to explore opportunities, connect with like-minded members",
  heroLines: [
    "Dine with Founders.",
    "Build what's next.",
    "Real conversations.",
    "No pitches. Just dinner.",
  ],
};
